<template>
  <TheLoader v-if="loading" size="xlarge" :class="$attrs.class" />
  <div
    v-show="!loading"
    class="relative flex flex-col w-full"
    :class="[
      $attrs.class,
      {
        '!overflow-hidden': sendLoading,
      },
    ]"
  >
    <slot v-if="sendLoading" name="sendLoading">
      <div
        class="absolute inset-0 size-full bg-white/90 backdrop-blur flex flex-col gap-y-5 items-center justify-center font-semibold"
      >
        <TheLoader class="h-fit" size="xlarge" />
        <span>Sending</span>
      </div>
    </slot>

    <template v-if="!showSuccess">
      <slot name="header" />

      <div :id="containerId" ref="formRef" class="bm-custom-form not-prose" />

      <slot name="footer" />
    </template>
    <slot
      v-else
      name="success"
      :refresh-form="refreshForm"
      :close-form="closeForm"
    >
      <DefaultSuccessMessage
        :success-message-classes="successMessageClasses"
        :message="props.successMessage"
        :with-close-button="withCloseButton"
        @close="closeForm"
      />
    </slot>
  </div>
</template>

<script setup lang="ts">
import TheLoader from '~/components/TheLoader.vue'
import DefaultSuccessMessage from '~/components/forms/DefaultSuccessMessage.vue'

import { useAuthStore } from '~/stores/auth'
import type { TCommon } from '~/types/app.type'

type TProps = {
  formId: string
  portalId?: string
  region?: string
  withCloseButton?: boolean
  successMessage?: string
  formType?: string
  formName?: string
  successMessageClasses?: string
}

type TBeforeSubmit = {
  form: HTMLFormElement
  values: TCommon
}

type TSubmitted = {
  form: HTMLFormElement
  values: TCommon
  refreshForm: () => void
}

type AllowedInputElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement

const { $rudderstack } = useNuxtApp()
const config = useRuntimeConfig()
const authStore = useAuthStore()

const userEmail = computed(() => authStore.userInfo?.email || '')

const props = withDefaults(defineProps<TProps>(), {
  withCloseButton: true,
  formType: 'contact-form',
  formName: 'Contact Us',
  successMessageClasses: '',
})

const emit = defineEmits<{
  'on-submitted': [args: TSubmitted]
  'on-before-init': []
  'on-ready': [form: HTMLFormElement]
  'on-before-submit': [args: TBeforeSubmit]
  close: []
}>()

const hasStartedFillingForm = ref(false)
const formRef = ref<HTMLDivElement | null>(null)
const containerId = useId()
const formInstanceId = `hs-form-${containerId}`
const loading = ref(true)
const sendLoading = ref(false)
const showSuccess = ref(false)

const hsPortalId = computed(
  () => props.portalId ?? config.public.hubspotPortalId,
)
const hsRegion = computed(() => props.region ?? config.public.hubspotRegion)

const onFormSubmitted = (
  form: HTMLFormElement,
  { submissionValues: values },
) => {
  sendLoading.value = false
  showSuccess.value = true
  emit('on-submitted', { form, values, refreshForm })

  $rudderstack.trackFormSubmissions({
    form_id: props.formId,
    form_type: props.formType,
    form_name: props.formName,
  })
}

const onBeforeFormInit = () => {
  loading.value = true
  emit('on-before-init')
}

const setOnInputWebsiteUrl = (form: HTMLFormElement) => {
  if (import.meta.server) return

  const websiteInput: HTMLInputElement = form.querySelector(
    'input[name*="form_completed_on"]',
  )
  if (websiteInput) websiteInput.value = window.location.href
}

const setEmailWhenHidden = (form: HTMLFormElement) => {
  if (import.meta.server) return

  const emailInput: HTMLInputElement = form.querySelector('input[name="email"]')

  if (!emailInput || emailInput?.type !== 'hidden') return

  emailInput.value = userEmail.value
}

const setCountryName = (form: HTMLFormElement) => {
  if (import.meta.server) return

  const countryInput: HTMLSelectElement = form.querySelector(
    'select[id^="phone_ext"]',
  )
  const countryName: HTMLInputElement = form.querySelector(
    'input[name*="hs_country_region_code"]',
  )

  if (!countryInput || !countryName) return

  countryName.value = countryInput.value
}

const observeFormChanges = (form: HTMLFormElement) => {
  const addListenersToInputs = () => {
    form
      .querySelectorAll<AllowedInputElement>('input, textarea, select')
      .forEach((input) => {
        if (!input.dataset.listenerAdded) {
          // Prevent duplicate listeners
          input.dataset.listenerAdded = 'true'

          // Listen for value changes
          input.addEventListener('input', (event: Event) => {
            // const target = event.target as AllowedInputElement

            trackInputChanges(event)
            trackFormStart()
          })
        }
      })
  }

  // Attach listeners to existing inputs
  addListenersToInputs()

  // Observe DOM for newly added inputs
  const observer = new MutationObserver(() => {
    addListenersToInputs()
  })

  observer.observe(form, { childList: true, subtree: true })
}

const trackFormStart = () => {
  if (!hasStartedFillingForm.value) {
    hasStartedFillingForm.value = true

    $rudderstack.trackFormStart({
      form_id: props.formId,
      form_type: props.formType,
      form_name: props.formName,
    })
  }
}

function parseFormInput(
  event: Event,
): { name: string; value: string; fieldType: string } | null {
  const target = event.target as HTMLElement

  if (target instanceof HTMLInputElement) {
    return { fieldType: target.type, name: target.name, value: target.value }
  }
  if (target instanceof HTMLTextAreaElement) {
    return { fieldType: 'textarea', name: target.name, value: target.value }
  }
  if (target instanceof HTMLSelectElement) {
    return { fieldType: 'select', name: target.name, value: target.value }
  }

  return null // Not a form element
}

const onFormReady = (form: HTMLFormElement) => {
  setOnInputWebsiteUrl(form)
  setEmailWhenHidden(form)

  emit('on-ready', form)
  loading.value = false
  showSuccess.value = false

  nextTick(() => {
    observeFormChanges(form)
  })
}

const trackInputChanges = (event: Event) => {
  const { name, value, fieldType } = parseFormInput(event)

  if (!name || !value || !fieldType) return

  $rudderstack.trackFormFieldChange({
    form_id: props.formId,
    form_type: props.formType,
    form_name: props.formName,
    field_name: name,
    field_type: fieldType,
    field_value: value,
  })
}

const onBeforeFormSubmit = (form: HTMLFormElement, values: TCommon) => {
  sendLoading.value = true
  setCountryName(form)
  emit('on-before-submit', { form, values })
}

const loadForm = (formId: string) => {
  if (import.meta.server || !window?.hbspt) return

  nextTick(() => {
    window?.hbspt.forms.create({
      formInstanceId,
      formId,
      region: hsRegion.value,
      portalId: hsPortalId.value,
      target: `#${containerId}`,
      onFormSubmitted,
      onBeforeFormSubmit,
      onBeforeFormInit,
      onFormReady,
    })
  })
}

const refreshForm = () => {
  loadForm(props.formId)
  // loading.value = false
  showSuccess.value = false
}

const closeForm = () => {
  refreshForm()
  emit('close')
}

watch(
  () => props.formId,
  (formId) => {
    loadForm(formId)
  },
  { immediate: true },
)
</script>
